import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownRight, Plus, CreditCard, Clock, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

interface Transaction {
  id: string;
  amount: number;
  type: 'purchase' | 'deduction' | 'refund';
  description: string;
  created_at: string;
}

export default function WalletPage() {
  const { activeUserId } = useAuth();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeUserId) fetchData();
  }, [activeUserId]);

  const fetchData = async () => {
    try {
      const { data: rec } = await supabase
        .from('recruiters')
        .select('tokens_balance')
        .eq('id', activeUserId!)
        .single();
      setBalance(rec?.tokens_balance || 0);

      const { data: txns } = await supabase
        .from('token_transactions')
        .select('*')
        .eq('recruiter_id', activeUserId!)
        .order('created_at', { ascending: false })
        .limit(50);
      setTransactions(txns || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const typeConfig: Record<string, { icon: React.ReactNode; color: string; bg: string; label: string }> = {
    purchase: { icon: <ArrowUpRight size={16} />, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Purchase' },
    deduction: { icon: <ArrowDownRight size={16} />, color: 'text-red-500', bg: 'bg-red-50', label: 'Deduction' },
    refund: { icon: <TrendingUp size={16} />, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Refund' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in">
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>Wallet</h1>
        <p className="text-slate-500 mt-1 text-[15px]">Manage your tokens and view transaction history.</p>
      </div>

      {/* Balance + Quick Actions */}
      <div className="grid md:grid-cols-3 gap-5">
        {/* Balance card */}
        <div className="md:col-span-2 bg-slate-950 rounded-2xl p-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          <div className="relative">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Token Balance</p>
            <p className="text-5xl font-black tracking-tight leading-none mb-2">{balance.toLocaleString()}</p>
            <p className="text-slate-400 text-[14px]">tokens available</p>
          </div>
        </div>

        {/* Buy tokens */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 flex flex-col items-center justify-center text-center shadow-sm hover:shadow-md transition-shadow">
          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
            <Plus size={24} className="text-blue-600" />
          </div>
          <p className="text-[15px] font-bold text-slate-900 mb-1">Buy Tokens</p>
          <p className="text-[12px] text-slate-400 mb-4">Purchase tokens to unlock applicants</p>
          <button className="w-full bg-slate-950 text-white text-[13px] font-semibold py-2.5 rounded-xl hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
            <CreditCard size={15} />
            Purchase
          </button>
        </div>
      </div>

      {/* Transactions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Transaction History</h2>
          <span className="text-[12px] text-slate-400 font-medium">{transactions.length} transactions</span>
        </div>

        {transactions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
            <Clock size={40} className="mx-auto text-slate-200 mb-3" />
            <p className="text-slate-400 text-[14px]">No transactions yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
            {transactions.map((tx, i) => {
              const cfg = typeConfig[tx.type] || typeConfig.deduction;
              return (
                <div key={tx.id} className={`flex items-center gap-4 px-5 py-4 ${i > 0 ? 'border-t border-slate-100' : ''} hover:bg-slate-50/50 transition-colors`}>
                  <div className={`w-9 h-9 rounded-xl ${cfg.bg} flex items-center justify-center ${cfg.color} shrink-0`}>
                    {cfg.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-800 truncate">{tx.description || cfg.label}</p>
                    <p className="text-[11px] text-slate-400">{format(new Date(tx.created_at), 'MMM d, yyyy · h:mm a')}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-[14px] font-bold ${tx.type === 'deduction' ? 'text-red-500' : 'text-emerald-600'}`}>
                      {tx.type === 'deduction' ? '-' : '+'}{Math.abs(tx.amount)}
                    </p>
                    <p className="text-[10px] text-slate-400 font-medium uppercase">{cfg.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
